import React from 'react';
import { Layout, Code, Zap, Layers, Cpu, CheckCircle2, ArrowRight, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

export function Services() {
    return (
        <div className="bg-background min-h-screen">
            {/* Hero Section */}
            <section className="pt-24 pb-12 px-4 shadow-sm border-b border-border bg-card/30">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-black text-foreground mb-4">Professional Services</h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Explore our full suite of AI image processing tools designed for modern creative workflows.
                    </p>
                </div>
            </section>

            {/* Main Services Grid */}
            <section className="py-12 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-32">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-6 uppercase tracking-wider">
                            <Zap className="h-3 w-3" /> Flagship Service
                        </div>
                        <h2 className="text-3xl font-bold text-foreground mb-6">Automated Background Removal</h2>
                        <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                            Our proprietary AI models detect edges with pixel-perfect accuracy. From complex hair to transparent objects, we provide the cleanest cutouts in the industry.
                        </p>
                        <ul className="space-y-4 mb-8">
                            <li className="flex items-center gap-3 text-foreground font-medium">
                                <CheckCircle2 className="h-5 w-5 text-green-500" /> High-Resolution Exports
                            </li>
                            <li className="flex items-center gap-3 text-foreground font-medium">
                                <CheckCircle2 className="h-5 w-5 text-green-500" /> Batch Processing Support
                            </li>
                            <li className="flex items-center gap-3 text-foreground font-medium">
                                <CheckCircle2 className="h-5 w-5 text-green-500" /> Real-time Preview API
                            </li>
                        </ul>
                        <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition-opacity">
                            Start Processing <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="aspect-square bg-card border border-border rounded-3xl flex items-center justify-center p-8">
                            <Layout className="h-16 w-16 text-primary" />
                        </div>
                        <div className="aspect-square bg-primary/5 border border-primary/20 rounded-3xl mt-12 flex items-center justify-center p-8">
                            <Cpu className="h-16 w-16 text-primary" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center flex-row-reverse">
                    <div className="md:order-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-6 uppercase tracking-wider">
                            <Code className="h-3 w-3" /> Developer First
                        </div>
                        <h2 className="text-3xl font-bold text-foreground mb-6">Scalable Developer API</h2>
                        <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                            Integrate our powerful engine directly into your app. with our REST API, you can automate background removal for thousands of users without maintaining complex infrastructure.
                        </p>
                        <Link to="/developer" className="text-primary font-bold hover:underline flex items-center gap-2">
                            Read API Docs <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="md:order-1 bg-card border border-border rounded-3xl p-12 overflow-hidden relative">
                        <pre className="text-sm text-primary font-mono lowercase">
                            {`curl -X POST https://api.bgremover.ai/v1/remove \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "file=@photo.jpg"`}
                        </pre>
                        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
                    </div>
                </div>
            </section>

            {/* Plan Details Section */}
            <section className="py-12 bg-card/30 border-t border-border">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-foreground">Understanding Our Plans</h2>
                        <p className="text-muted-foreground mt-4">We offer flexible pricing options to suit every scale.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="p-8 bg-card border border-border rounded-3xl shadow-sm">
                            <h3 className="text-xl font-bold text-foreground mb-4">Credit Packs (Starter & Growth)</h3>
                            <p className="text-muted-foreground mb-6">
                                Best for individuals and occasional users. Pre-pay for a fixed number of credits that never expire.
                            </p>
                            <ul className="space-y-3 text-sm text-muted-foreground mb-8">
                                <li className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Pay once, use anytime</li>
                                <li className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Perfect for batches up to 450 images</li>
                                <li className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Full access to web editor</li>
                            </ul>
                        </div>

                        <div className="p-8 bg-card border border-border rounded-3xl shadow-sm relative overflow-hidden">
                            <div className="absolute top-4 right-4 text-[10px] font-black bg-primary/10 text-primary px-2 py-1 rounded">MOST VERSATILE</div>
                            <h3 className="text-xl font-bold text-foreground mb-4">Enterprise (Pay-As-You-Go)</h3>
                            <p className="text-muted-foreground mb-6">
                                Designed for high-volume developers. Includes a small yearly membership fee and low per-call rates.
                            </p>
                            <ul className="space-y-3 text-sm text-muted-foreground mb-8">
                                <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> $0.02 per successful API call</li>
                                <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Monthly automated billing</li>
                                <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Highest priority processing queue</li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-12 text-center">
                        <Link to="/pricing" className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4">
                            View exact pricing breakdown
                        </Link>
                    </div>
                </div>
            </section>

            {/* Barcodely Promotion Section */}
            <section className="py-12 px-4 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative p-8 md:p-12 rounded-3xl bg-card/50 backdrop-blur-xl border border-border overflow-hidden">
                            <div className="grid md:grid-cols-2 gap-12 items-center">
                                <div className="space-y-6">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary uppercase tracking-wider">
                                        Partner Product
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                                        Meet Barcodely: Global Product Data <span className="text-primary italic">Simplified.</span>
                                    </h2>
                                    <p className="text-lg text-muted-foreground leading-relaxed">
                                        Barcodely is a leading provider of global product data, specializing in barcode lookup and API services. Easily integrate comprehensive product information into your applications, PoS systems, and inventory tools.
                                    </p>
                                    <div className="flex flex-wrap gap-4 pt-4">
                                        <a
                                            href="https://barcodely.cloud"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-all shadow-lg shadow-primary/20"
                                        >
                                            Explore Barcodely.cloud
                                            <ArrowRight className="h-4 w-4" />
                                        </a>
                                    </div>
                                </div>
                                <div className="relative">
                                    <div className="aspect-video rounded-2xl bg-white/50 backdrop-blur-sm border border-border/50 p-4 flex items-center justify-center overflow-hidden">
                                        <img
                                            src="/barcodely_graphic.png"
                                            alt="Barcodely Scanner"
                                            className="w-full h-full object-contain mix-blend-multiply hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    {/* Decorative elements */}
                                    <div className="absolute -top-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
                                    <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-accent/10 rounded-full blur-3xl"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
